import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Coins, Loader2, CheckCircle2, X, Filter } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const fmtFjd = (n) => `FJ$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Math.round((n || 0) * 100) / 100)}`;

export default function AdminPayoutsTab() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const url = filter === "all" ? "/admin/payouts" : `/admin/payouts?status=${filter}`;
            const res = await api.get(url);
            setItems(res.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load payouts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const markPaid = async (p) => {
        if (!window.confirm(`Mark ${fmtFjd(p.amount_fjd)} to ${p.payee_name || p.payee_email} as paid?`)) return;
        setActing(p.id);
        try {
            const { data } = await api.patch(`/admin/payouts/${p.id}/mark-paid`, {});
            if (data.stripe_transfer_id && !String(data.stripe_transfer_id).startsWith("failed:")) {
                toast.success(`Paid via Stripe Transfer · ${data.stripe_transfer_id.slice(0, 16)}…`);
            } else if (String(data.stripe_transfer_id || "").startsWith("failed:")) {
                toast.warning("Marked paid — Stripe Transfer skipped (no connected account or error).");
            } else {
                toast.success("Marked paid.");
            }
            await reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not mark paid");
        } finally {
            setActing(null);
        }
    };

    const cancel = async (p) => {
        if (!window.confirm(`Cancel ${fmtFjd(p.amount_fjd)} payout to ${p.payee_name || p.payee_email}?`)) return;
        setActing(p.id);
        try {
            await api.patch(`/admin/payouts/${p.id}/cancel`);
            toast.success("Cancelled.");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel");
        } finally {
            setActing(null);
        }
    };

    const totalPending = items
        .filter((i) => i.status === "pending")
        .reduce((s, i) => s + (i.amount_fjd || 0), 0);

    return (
        <div data-testid="admin-payouts-tab" className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-xl lg:text-2xl font-bold text-foreground flex items-center gap-2">
                        <Coins className="h-5 w-5 text-coral" /> JV Payout Ledger
                    </h2>
                    <p className="font-app text-xs text-muted-foreground mt-0.5">
                        {filter === "pending"
                            ? `${items.length} pending · ${fmtFjd(totalPending)} unpaid`
                            : `${items.length} ${filter === "all" ? "total" : filter}`}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-36 bg-background text-foreground" data-testid="payouts-filter-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-coral mx-auto" /></div>
            ) : items.length === 0 ? (
                <Card className="p-6 text-center bg-card">
                    <p className="font-app text-sm text-muted-foreground">No {filter === "all" ? "" : filter} payouts.</p>
                </Card>
            ) : (
                <div className="space-y-2" data-testid="admin-payouts-rows">
                    {items.map((p) => (
                        <Card key={p.id} className="p-4 bg-card flex items-center gap-3 flex-wrap" data-testid={`admin-payout-${p.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-display text-sm font-bold text-foreground truncate">
                                        {p.payee_name || p.payee_email}
                                    </span>
                                    <Badge variant="outline" className="text-[10px]">{p.role === "primary" ? "Primary" : "Co-agent"}</Badge>
                                    <StatusBadge status={p.status} />
                                </div>
                                <div className="font-app text-[11px] text-muted-foreground truncate">
                                    {p.jv_title} · closed {new Date(p.closed_at).toLocaleDateString()}
                                    {p.paid_at && ` · paid ${new Date(p.paid_at).toLocaleDateString()}`}
                                </div>
                                {p.stripe_transfer_id && !String(p.stripe_transfer_id).startsWith("failed:") && (
                                    <div className="font-app text-[10px] text-fiji-green mt-0.5">Stripe Transfer · {String(p.stripe_transfer_id).slice(0, 18)}…</div>
                                )}
                            </div>
                            <div className="text-right">
                                <div className="font-display text-lg font-bold text-foreground tabular-nums">{fmtFjd(p.amount_fjd)}</div>
                                <div className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">FJD</div>
                            </div>
                            {p.status === "pending" && (
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <Button
                                        size="sm"
                                        onClick={() => markPaid(p)}
                                        disabled={acting === p.id}
                                        data-testid={`admin-payout-mark-paid-${p.id}`}
                                        className="bg-fiji-green hover:bg-fiji-green/90 text-white font-app"
                                    >
                                        {acting === p.id && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Mark paid
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => cancel(p)}
                                        disabled={acting === p.id}
                                        data-testid={`admin-payout-cancel-${p.id}`}
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10 font-app"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    if (status === "paid") return <Badge className="bg-fiji-green text-white text-[10px]">Paid</Badge>;
    if (status === "cancelled") return <Badge variant="outline" className="text-[10px] border-destructive/30 text-destructive">Cancelled</Badge>;
    return <Badge variant="outline" className="text-[10px] border-coral/40 text-coral">Pending</Badge>;
}
